import React, { useState } from "react";

const SupportPage: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How does Posture Good work?",
      answer:
        "Posture Good sends you gentle posture reminders at regular intervals during your specified time range. Simply set your name and notification hours when you first open the app, and it will automatically send you helpful reminders throughout the day.",
    },
    {
      question: "How can I change my notification times?",
      answer:
        "You can modify your notification hours and reminder frequency anytime by tapping the 'Settings' button on the main screen. Changes take effect immediately.",
    },
    {
      question: "Which devices are supported?",
      answer:
        "Posture Good works on iOS (iPhone and iPad) and Android devices. It requires iOS 13.0+ or Android 8.0+ to run properly.",
    },
    {
      question: "Is the app free?",
      answer:
        "Yes, Posture Good is completely free to use. There are no ads, no in-app purchases, and no subscription fees.",
    },
    {
      question: "Is my data safe?",
      answer:
        "Absolutely! All your data is stored locally on your device. We don't collect or transmit any personal information to our servers. Only your name and reminder preferences are saved on your device.",
    },
    {
      question: "Why am I not receiving notifications?",
      answer:
        "Please check your device's notification settings for Posture Good. Make sure notifications are enabled and not blocked by Do Not Disturb mode. Also verify that your reminder hours match your current time.",
    },
    {
      question: "Can I customize reminder messages?",
      answer:
        "The app includes a variety of encouraging posture reminder messages that rotate automatically. Currently, custom messages are not available, but we're considering this feature for future updates.",
    },
    {
      question: "How often will I receive reminders?",
      answer:
        "You can set reminder intervals from 1 minute to 90 minutes in the Settings screen. The default is every 30 minutes, but you can adjust this to your preference.",
    },
  ];

  const features = [
    {
      icon: "🔔",
      title: "Smart Notifications",
      description:
        "Gentle reminders at customizable intervals to help you maintain good posture throughout the day.",
    },
    {
      icon: "⏰",
      title: "Flexible Scheduling",
      description:
        "Set your active hours so you only receive reminders when you're working or need them most.",
    },
    {
      icon: "🧘‍♀️",
      title: "Posture Guidance",
      description:
        "Quick posture check tips and guidance to help you improve your posture instantly.",
    },
    {
      icon: "📱",
      title: "Simple Interface",
      description:
        "Clean, intuitive design that's easy to set up and use without complicated configurations.",
    },
    {
      icon: "🎯",
      title: "Personalized Experience",
      description:
        "Personalized reminders with your name and customizable frequency to match your needs.",
    },
    {
      icon: "🔒",
      title: "Privacy First",
      description:
        "All data stays on your device. No accounts required, no data collection, complete privacy.",
    },
  ];

  return (
    <div>
      {/* Floating Elements */}
      <div className="floating-element floating-1"></div>
      <div className="floating-element floating-2"></div>
      <div className="floating-element floating-3"></div>

      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <span className="logo-icon">🧘‍♀️</span>
              Posture Good
            </div>
            <div style={{ color: "rgba(255, 255, 255, 0.8)" }}>
              Support & Help Center
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <h1>Welcome to Posture Good</h1>
          <p>
            Your personal posture reminder app that helps you maintain healthy
            posture throughout the day with gentle, customizable notifications.
          </p>
          <div className="app-badges">
            <a href="#" className="badge">
              📱 Available on iOS
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section">
        <div className="container">
          <div className="section-content">
            <h2>App Features</h2>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container">
          <div className="section-content">
            <h2>Frequently Asked Questions</h2>
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  {faq.question}
                  <span>{openFaq === index ? "−" : "+"}</span>
                </button>
                <div
                  className={`faq-answer ${openFaq === index ? "" : "hidden"}`}
                >
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section">
        <div className="container">
          <div className="section-content">
            <h2>Get Support</h2>
            <div className="contact-grid">
              <div className="contact-card">
                <div className="contact-icon">📧</div>
                <h3>Email Support</h3>
                <p>Have a question or need help?</p>
                <a href="mailto:gurkanyigitgr@icloud.com">
                  gurkanyigitgr@icloud.com
                </a>
              </div>
              <div className="contact-card">
                <div className="contact-icon">🐛</div>
                <h3>Report a Bug</h3>
                <p>Found an issue? Let us know!</p>
                <a href="mailto:gurkanyigitgr@icloud.com">
                  gurkanyigitgr@icloud.com
                </a>
              </div>
              <div className="contact-card">
                <div className="contact-icon">💡</div>
                <h3>Feature Requests</h3>
                <p>Suggest new features</p>
                <a href="mailto:gurkanyigitgr@icloud.com">
                  gurkanyigitgr@icloud.com
                </a>
              </div>
            </div>

            <div
              style={{
                textAlign: "center",
                marginTop: "2rem",
                padding: "1.5rem",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
              }}
            >
              <h3 style={{ color: "#FFFFFF", marginBottom: "1rem" }}>
                Quick Response Promise
              </h3>
              <p style={{ color: "rgba(255, 255, 255, 0.85)" }}>
                We typically respond to all support emails within 24 hours. For
                urgent issues, please include "URGENT" in your subject line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* App Info Section */}
      <section className="section">
        <div className="container">
          <div className="section-content">
            <h2>App Information</h2>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">ℹ️</div>
                <h3>Version</h3>
                <p>
                  Current version: 1.0.0
                  <br />
                  Latest update: August 2025
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📋</div>
                <h3>Requirements</h3>
                <p>
                  iOS
                  <br />
                  Compatible with iPhone and iPad
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🏆</div>
                <h3>Our Mission</h3>
                <p>
                  Help people maintain better posture and reduce back pain
                  through gentle, consistent reminders.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2025 Posture Good. All rights reserved.</p>
          <p style={{ marginTop: "0.5rem", fontSize: "0.9rem" }}>
            Made with ❤️ to help improve your posture and health.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SupportPage;
